import React from 'react';
import { Eye } from 'lucide-react';
import { StatusOption, SideSelector } from './ExamFormControls';

const CRANIAL_NERVES = [
  { field: 'cn_i_olfactory', label: 'CN I - Olfactory' },
  { field: 'cn_ii_optic', label: 'CN II - Optic (Menace)' },
  { field: 'cn_ii_plr_direct', label: 'Direct PLR' },
  { field: 'cn_ii_plr_indirect', label: 'Indirect PLR' },
  { field: 'cn_iii_oculomotor', label: 'CN III - Oculomotor' },
  { field: 'cn_iv_trochlear', label: 'CN IV - Trochlear' },
  { field: 'cn_v_trigeminal_sensory', label: 'CN V - Trigeminal (Sensory)' },
  { field: 'cn_v_trigeminal_motor', label: 'CN V - Trigeminal (Motor)' },
  { field: 'cn_vi_abducens', label: 'CN VI - Abducens' },
  { field: 'cn_vii_facial', label: 'CN VII - Facial' },
  { field: 'cn_viii_vestibulocochlear', label: 'CN VIII - Vestibulocochlear' },
  { field: 'cn_ix_glossopharyngeal', label: 'CN IX - Glossopharyngeal' },
  { field: 'cn_x_vagus', label: 'CN X - Vagus' },
  { field: 'cn_xi_accessory', label: 'CN XI - Accessory' },
  { field: 'cn_xii_hypoglossal', label: 'CN XII - Hypoglossal' },
];

const CranialNervesSection = ({ formData, handleChange, readOnly }) => {
  const clearCnViii = () => {
    handleChange('nystagmus_type', '');
    handleChange('nystagmus_fast_phase', '');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 p-3 bg-[#F8F7F4] rounded-lg">
        <Eye className="w-4 h-4 text-[#4B6651]" />
        <span className="text-sm text-[#5C5F5C]">
          Assess each cranial nerve. Mark as abnormal if deficits are noted.
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CRANIAL_NERVES.map(({ field, label }) => (
          <div key={field} className="p-3 bg-[#F8F7F4] rounded-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="text-xs sm:text-sm text-[#2A2C2A] font-medium">{label}</span>
              <div className="flex gap-1 flex-wrap">
                <StatusOption
                  value="normal"
                  label="Nml"
                  selected={formData[field] === 'normal'}
                  onChange={(v) => {
                    handleChange(field, v);
                    handleChange(`${field}_side`, '');
                    if (field === 'cn_viii_vestibulocochlear') clearCnViii();
                  }}
                  readOnly={readOnly}
                />
                <StatusOption
                  value="abnormal"
                  label="Abn"
                  selected={formData[field] === 'abnormal'}
                  onChange={(v) => handleChange(field, v)}
                  readOnly={readOnly}
                />
                <StatusOption
                  value="absent"
                  label="Abs"
                  selected={formData[field] === 'absent' || formData[field] === 'absent bilateral'}
                  onChange={(v) => handleChange(field, v)}
                  readOnly={readOnly}
                />
                <StatusOption
                  value="not_tested"
                  label="N/T"
                  selected={formData[field] === 'not_tested'}
                  onChange={(v) => {
                    handleChange(field, v);
                    handleChange(`${field}_side`, '');
                    if (field === 'cn_viii_vestibulocochlear') clearCnViii();
                  }}
                  readOnly={readOnly}
                />
                <StatusOption
                  value="unable_to_evaluate"
                  label="U/E"
                  selected={formData[field] === 'unable_to_evaluate'}
                  onChange={(v) => {
                    handleChange(field, v);
                    handleChange(`${field}_side`, '');
                    if (field === 'cn_viii_vestibulocochlear') clearCnViii();
                  }}
                  readOnly={readOnly}
                />
              </div>
            </div>
            <SideSelector
              field={field}
              show={formData[field] === 'abnormal' || formData[field] === 'absent' || formData[field] === 'absent bilateral'}
              formData={formData}
              handleChange={handleChange}
              readOnly={readOnly}
            />
            {/* Nystagmus sub-fields for CN VIII */}
            {field === 'cn_viii_vestibulocochlear' && (formData[field] === 'abnormal' || formData[field] === 'absent') && (
              <div className="mt-2 space-y-2 pl-1" data-testid="nystagmus-fields">
                <div className="space-y-1">
                  <span className="text-xs text-[#5C5F5C]">Nystagmus type:</span>
                  <div className="flex gap-1 flex-wrap">
                    {[
                      { value: 'horizontal', label: 'Horizontal' },
                      { value: 'vertical', label: 'Vertical' },
                      { value: 'rotary', label: 'Rotary' },
                    ].map(({ value, label: lbl }) => (
                      <button
                        key={value}
                        type="button"
                        disabled={readOnly}
                        onClick={() => !readOnly && handleChange('nystagmus_type', formData.nystagmus_type === value ? '' : value)}
                        className={`px-2 py-0.5 text-xs rounded transition-all ${
                          formData.nystagmus_type === value
                            ? 'bg-[#C06144] text-white'
                            : 'bg-white border border-[#E5E4E0] text-[#5C5F5C] hover:bg-[#F8F7F4]'
                        } ${readOnly ? 'cursor-default opacity-80' : ''}`}
                        data-testid={`nystagmus-${value}`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-[#5C5F5C]">Fast phase:</span>
                  <div className="flex gap-1">
                    {[
                      { value: 'left', label: 'Left' },
                      { value: 'right', label: 'Right' },
                    ].map(({ value, label: lbl }) => (
                      <button
                        key={value}
                        type="button"
                        disabled={readOnly}
                        onClick={() => !readOnly && handleChange('nystagmus_fast_phase', formData.nystagmus_fast_phase === value ? '' : value)}
                        className={`px-2 py-0.5 text-xs rounded transition-all ${
                          formData.nystagmus_fast_phase === value
                            ? 'bg-[#C06144] text-white'
                            : 'bg-white border border-[#E5E4E0] text-[#5C5F5C] hover:bg-[#F8F7F4]'
                        } ${readOnly ? 'cursor-default opacity-80' : ''}`}
                        data-testid={`nystagmus-fast-${value}`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CranialNervesSection;
